import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { AppThunk } from '../../shared/store'
import { BaseMapFilterEnum, IActiveFilter, IBaseMapFilter, ICoordinates, IMapLayer, IMapLayerData, } from '../../shared/models/map_models'
import { getMapDataForLayerAPI, getMapLayersAPI } from '../../shared/api/map_api'
import { IMapFilter, IMapLayerFeatures } from './map-models'
import * as paramUtilities from './param-state-utility'


export interface MapState {
  mapLayerData: Array<IMapLayerData> | null,
  loading: boolean,
  boundingBox: Array<ICoordinates> | null,
  mapLayers: Array<IMapLayer> | null,
  filters: Array<IMapFilter> | null
  layerFeatureCollections: Array<IMapLayerFeatures> | null
}

const initialState: MapState = {
    mapLayerData: null,
    loading: true,
    boundingBox: null,
    mapLayers: null,
    layerFeatureCollections: null,
    filters: null,
}

export const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    setMapLayerData: (state, action: PayloadAction<Array<IMapLayerData>>) => {
      if (state.mapLayerData){
        const currentMapLayerData = state.mapLayerData
        action.payload.forEach((layer) => {
          const existingLayerIndex = currentMapLayerData.findIndex((x) => x.id === layer.id)
          if (existingLayerIndex >= 0)
            currentMapLayerData[existingLayerIndex] = layer
          else 
            currentMapLayerData.push(layer)
        })

        state.mapLayerData = currentMapLayerData
      } else {
        state.mapLayerData = action.payload
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setMapLayers: (state, action: PayloadAction<Array<IMapLayer>>) => {
      state.mapLayers = action.payload
    },
    setBoundingBox: (state, action: PayloadAction<Array<ICoordinates>>) => {
        state.boundingBox = action.payload
    },
    setMapLayerVisibility: (state, action: PayloadAction<number>) => {
      if(state.mapLayers){
        const newState = [...state.mapLayers]
        newState.map((x) => {
            if (x.id === action.payload){
              x.visible = !x.visible
            }

            return x
        })
        state.mapLayers = newState
      }
    },
    setFilters: (state, action: PayloadAction<Array<IBaseMapFilter>>) => {
      const stateFromParams = paramUtilities.getFilterStateFromParams()
      const updatedFilters: Array<IMapFilter> = action.payload.map((mapFilter) => {
        const inParamState = stateFromParams.find((x) => x.filterId === mapFilter.id)
        const updatedFilter = {...mapFilter, value: mapFilter.type === BaseMapFilterEnum.RADIO ? false: ''}
        if(inParamState !== undefined){
          if(updatedFilter.type === 'radio')
            updatedFilter.value = inParamState.filterValue === 'true'
          else
            updatedFilter.value = inParamState.filterValue
        }
        else{
          paramUtilities.putMapFilterInParam(updatedFilter)
        }
        
        return updatedFilter
      })
      state.filters = updatedFilters
    },
    updateFilterValue: (state, action: PayloadAction<IMapFilter>) => {
      if(state.filters){
        const currentState = [...state.filters]
        const fitlerToUpdate = currentState.find((x) => x.id === action.payload.id)
        if(fitlerToUpdate !== undefined){
          const updatedState = currentState.map((x) => {
            const sameRadioGroup = (x.radio_group !== undefined && action.payload.radio_group !== undefined && 
              x.radio_group === action.payload.radio_group)
            if(x.id === action.payload.id)
              x.value = action.payload.value
            else if (  // Resetting all other radio buttons in the same group
              x.type === BaseMapFilterEnum.RADIO && x.layer_id === action.payload.layer_id && sameRadioGroup
            ){
              x.value = false
            }
            paramUtilities.putMapFilterInParam(x)
            return x
          })
           state.filters = updatedState
        }
      }
    },
    setLayerFeatureCollection: (state, action: PayloadAction<IMapLayerFeatures>) => {
      if (!state.layerFeatureCollections)
        state.layerFeatureCollections = [action.payload]
      else {
        const currentFeatureCollection = [...state.layerFeatureCollections]
        const existingIndex = state.layerFeatureCollections.findIndex((x) => x.layerId === action.payload.layerId)
        if(existingIndex >= 0)
          currentFeatureCollection[existingIndex] = action.payload
        else 
          currentFeatureCollection.push(action.payload)
        state.layerFeatureCollections = currentFeatureCollection
      }
    },
  },
})


export const fetchMapLayerData = (): AppThunk => async (dispatch, getState) => {

  const state = getState().map
  dispatch(setLoading(true))

  if (state.mapLayers === null)
    return
  try {
    const updatedMapLayerData: Array<IMapLayerData> = []
    // Getting data for all layers in parallel
    await Promise.all(state.mapLayers.map(async(layer) => {
      if(layer.visible)
        try{ 
          const layerFilters = state.filters?.filter((x) => x.layer_id === layer.id)
          const activeFilters: Array<IActiveFilter> = []
          layerFilters?.forEach((x) => {
            if(x.value !== ''){
              let valueToUse: string | boolean = x.value
              if (x.type === 'radio'){
                valueToUse = (x.value === true)
                if(valueToUse)
                  activeFilters.push({id: x.id, value: valueToUse})
              } else if (x.type === BaseMapFilterEnum.CHECKBOX){
                if (typeof x.value === 'string')  // Edge case when value comes from param (happens on first load with a param value)
                  valueToUse = x.value === 'true'
                else
                  valueToUse = x.value
                activeFilters.push({id: x.id, value: valueToUse})
              }
              else {
                activeFilters.push({id: x.id, value: valueToUse})
              }
            }
          })
          const mapData = (await getMapDataForLayerAPI(state?.boundingBox || [], layer, activeFilters)).data
          const mapLayerData: IMapLayerData = {
            ...layer,
            data: mapData,
          }
          updatedMapLayerData.push(mapLayerData)
        } catch(e) {
          console.error("Error getting data for layer: ", layer)
          console.error(e)
          const mapLayerData: IMapLayerData = {
            ...layer,
            data: [],
          }
          updatedMapLayerData.push(mapLayerData)
        }
    }))

    dispatch(setMapLayerData(updatedMapLayerData))

    } catch(e) {
    } finally {
      dispatch(setLoading(false))
    }
  }

export const fetchMapLayers = (): AppThunk => async (dispatch) => {
  dispatch(setLoading(true))
  try {
    const baseMapLayers = (await getMapLayersAPI()).data
    let layerFilters: Array<IBaseMapFilter> = []
    const mapLayers: Array<IMapLayer> = baseMapLayers.map((l) => {
      layerFilters = [...layerFilters, ...l.filters]
      return {
        ...l,
        visible: true
      }
    })
    dispatch(setFilters(layerFilters))
    dispatch(setMapLayers(mapLayers))
  } catch(e) {
    console.error("Exception in getting map layers: ", e)
    dispatch(setMapLayers([]))
  } finally {
    dispatch(setLoading(false))
  }
}


export const { 
  setMapLayerData, 
  setLoading,
  setFilters,
  updateFilterValue,
  setBoundingBox,
  setMapLayers,
  setMapLayerVisibility,
  setLayerFeatureCollection
} = mapSlice.actions

export const sliceActions = mapSlice.actions

export default mapSlice.reducer